import { Component } from '@theme/component';
import { bindDeferredVideo } from '@theme/echt-deferred-video';

/**
 * Echt · Product gallery.
 *
 * Interaction only. The mosaic, the rail and the dash thumb are all CSS; this
 * component exists for the two things the stylesheet cannot do.
 *
 * 1. Gallery videos attach their sources only when visible (or explicitly
 *    played), retaining posters and respecting motion/data-saving preferences.
 *
 * 2. The mobile dash thumb where `timeline-scope` is unsupported. Without a
 *    fallback the thumb sits on the first dash forever, which looks broken
 *    rather than plain.
 *
 * @extends Component
 */
export default class EchtProductGallery extends Component {
  /** @type {Array<() => void>} */
  #teardown = [];

  connectedCallback() {
    super.connectedCallback();
    this.#setUpVideo();
    this.#setUpIndicatorFallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const off of this.#teardown) off();
    this.#teardown = [];
  }

  /** @returns {HTMLVideoElement[]} */
  get videos() {
    return /** @type {HTMLVideoElement[]} */ (Array.from(this.querySelectorAll('video.echt-gal__media')));
  }

  #setUpVideo() {
    for (const video of this.videos) {
      this.#teardown.push(bindDeferredVideo(video, {
        autoplay: this.dataset.autoplay === 'true',
        button: video.parentElement.querySelector('[data-video-play]'),
      }));
    }
  }

  #setUpIndicatorFallback() {
    // Where scroll timelines work, the thumb is pure CSS and this does nothing.
    if (typeof CSS !== 'undefined' && CSS.supports?.('animation-timeline: scroll()')) return;

    const scroller = /** @type {HTMLElement | null} */ (this.querySelector('.echt-gal__list'));
    if (!scroller) return;
    const counter = /** @type {HTMLElement | null} */ (this.querySelector('.echt-gal__counter span'));

    const update = () => {
      const travel = scroller.scrollWidth - scroller.clientWidth;
      this.style.setProperty('--echt-gal-progress', String(travel > 0 ? scroller.scrollLeft / travel : 0));
      if (counter && scroller.clientWidth > 0) {
        counter.textContent = String(Math.round(scroller.scrollLeft / scroller.clientWidth) + 1);
      }
    };

    scroller.addEventListener('scroll', update, { passive: true });
    this.#teardown.push(() => scroller.removeEventListener('scroll', update));
    update();
  }
}

if (!customElements.get('echt-product-gallery')) {
  customElements.define('echt-product-gallery', EchtProductGallery);
}
